import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Package, Clock, CheckCircle, XCircle, Loader2,
  Mail, MapPin, User, Calendar, CreditCard, ArrowRight,
  MessageCircle, ExternalLink
} from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../services/api';

const statusConfig = {
  pending: {
    label: 'Pending',
    color: 'text-yellow-400',
    bg: 'bg-yellow-400/10',
    border: 'border-yellow-400/20',
    dot: 'bg-yellow-400',
    icon: Clock,
    description: 'Your order has been received and is waiting to be processed.',
  },
  processing: {
    label: 'Processing',
    color: 'text-blue-400',
    bg: 'bg-blue-400/10',
    border: 'border-blue-400/20',
    dot: 'bg-blue-400',
    icon: Loader2,
    description: 'Your foreign TikTok account is being created. Hang tight!',
  },
  completed: {
    label: 'Completed',
    color: 'text-green-400',
    bg: 'bg-green-400/10',
    border: 'border-green-400/20',
    dot: 'bg-green-400',
    icon: CheckCircle,
    description: 'Your account is ready! Check your email for login details.',
  },
  cancelled: {
    label: 'Cancelled',
    color: 'text-red-400',
    bg: 'bg-red-400/10',
    border: 'border-red-400/20',
    dot: 'bg-red-400',
    icon: XCircle,
    description: 'This order has been cancelled.',
  },
};

const steps = ['Order Placed', 'Payment Confirmed', 'Account Creation', 'Account Ready'];

const TrackOrder = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [request, setRequest] = useState(null);
  const [error, setError] = useState('');

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');
    setRequest(null);

    try {
      const res = await api.get(`/requests/track?email=${encodeURIComponent(email.trim())}`);
      setRequest(res.data.request);
    } catch (err) {
      setError(err.response?.data?.error || 'No order found for this email');
    } finally {
      setLoading(false);
    }
  };

  const getStepIndex = () => {
    if (!request) return -1;
    if (request.status === 'cancelled') return -1;
    if (request.status === 'pending') return request.payment ? 1 : 0;
    if (request.status === 'processing') return 2;
    if (request.status === 'completed') return 3;
    return 0;
  };

  const currentStep = getStepIndex();
  const status = statusConfig[request?.status] || statusConfig.pending;
  const StatusIcon = status.icon;

  return (
    <div className="min-h-screen bg-gray-950 pt-24 pb-12 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 mb-4">
            <Package size={14} className="text-primary-400" />
            <span className="text-sm text-primary-300">Order Tracking</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Track Your Order</h1>
          <p className="text-gray-400">Enter your email to check the status of your foreign TikTok account</p>
        </motion.div>

        {/* Search Form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onSubmit={handleTrack}
          className="mb-8"
        >
          <div className="bg-gray-900/50 border border-gray-800/60 rounded-2xl p-6">
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="w-full bg-gray-800/50 border border-gray-700/50 rounded-xl pl-10 pr-4 py-3.5 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500/40 transition-all"
                />
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading}
                className="px-6 py-3.5 bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 text-white font-semibold rounded-xl transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
                Track
              </motion.button>
            </div>
            {error && (
              <motion.p
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-400 text-sm mt-3"
              >
                {error}
              </motion.p>
            )}
          </div>
        </motion.form>

        {/* Result */}
        <AnimatePresence>
          {request && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              {/* Status Card */}
              <div className={`bg-gray-900/50 border ${status.border} rounded-2xl p-6`}>
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-14 h-14 ${status.bg} rounded-2xl flex items-center justify-center`}>
                    <StatusIcon size={28} className={`${status.color} ${request.status === 'processing' ? 'animate-spin' : ''}`} />
                  </div>
                  <div>
                    <h2 className={`text-xl font-bold ${status.color}`}>{status.label}</h2>
                    <p className="text-sm text-gray-400">{status.description}</p>
                  </div>
                </div>

                {/* Progress Steps */}
                {request.status !== 'cancelled' && (
                  <div className="flex items-center justify-between mb-2">
                    {steps.map((step, i) => (
                      <div key={step} className="flex flex-col items-center flex-1">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold mb-2 transition-all ${
                          i <= currentStep
                            ? 'bg-primary-500 text-white'
                            : 'bg-gray-800 text-gray-500 border border-gray-700'
                        }`}>
                          {i < currentStep ? <CheckCircle size={16} /> : i + 1}
                        </div>
                        <span className={`text-[10px] text-center leading-tight ${
                          i <= currentStep ? 'text-primary-400' : 'text-gray-600'
                        }`}>{step}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Order Details */}
              <div className="bg-gray-900/50 border border-gray-800/60 rounded-2xl p-6">
                <h3 className="text-base font-semibold text-white mb-4">Order Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-gray-800/30 rounded-xl">
                    <User size={16} className="text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Name</p>
                      <p className="text-sm text-white">{request.fullName || request.email.split('@')[0]}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-800/30 rounded-xl">
                    <Mail size={16} className="text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="text-sm text-white truncate">{request.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-800/30 rounded-xl">
                    <MapPin size={16} className="text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Country</p>
                      <p className="text-sm text-white">{request.desiredCountry}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-800/30 rounded-xl">
                    <User size={16} className="text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Username</p>
                      <p className="text-sm text-white">{request.username || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-800/30 rounded-xl">
                    <Calendar size={16} className="text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Ordered</p>
                      <p className="text-sm text-white">{new Date(request.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-800/30 rounded-xl">
                    <Package size={16} className="text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Package</p>
                      <p className="text-sm text-white capitalize">{request.package}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Info */}
              {request.payment && (
                <div className="bg-gray-900/50 border border-gray-800/60 rounded-2xl p-6">
                  <h3 className="text-base font-semibold text-white mb-4">Payment Info</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-3 p-3 bg-gray-800/30 rounded-xl">
                      <CreditCard size={16} className="text-gray-500" />
                      <div>
                        <p className="text-xs text-gray-500">Amount</p>
                        <p className="text-sm text-white font-semibold">₦{request.payment.amount?.toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-800/30 rounded-xl">
                      <CheckCircle size={16} className="text-gray-500" />
                      <div>
                        <p className="text-xs text-gray-500">Payment</p>
                        <p className={`text-sm font-semibold capitalize ${
                          request.payment.status === 'success' ? 'text-green-400' : 'text-yellow-400'
                        }`}>{request.payment.status}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* CTA */}
              <div className="bg-gray-900/50 border border-gray-800/60 rounded-2xl p-6 text-center">
                <p className="text-gray-400 text-sm mb-4">Need help? Have questions about your order?</p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 text-white font-semibold rounded-xl transition-all"
                >
                  <MessageCircle size={18} /> Contact Support
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Empty State */}
        {!request && !loading && !error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-center py-12"
          >
            <div className="w-20 h-20 bg-gray-800/50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Package size={32} className="text-gray-600" />
            </div>
            <p className="text-gray-500">Enter your email above to track your order status</p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default TrackOrder;
